import { Slot } from '@radix-ui/react-slot'
import { Link } from '@remix-run/react'
import { cva, type VariantProps } from 'class-variance-authority'
import { type icons } from 'lucide-react'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { Icon } from '../tiptap/ui/Icon'

const cardVariants = cva('relative flex rounded-lg gap-4', {
	variants: {
		variant: {
			default: 'bg-primary-foreground border-1 border',
			filled: 'bg-primary border-color-primary-foreground border-1 border',
			empty: 'bg-muted border-color-primary-foreground border-dashed border',
			danger:
				'border-1 border text-red bg-primary-foreground border-destructive/30 bg-destructive/10',
		},
		direction: {
			default: 'flex-row',
			column: 'flex-col',
		},
		size: {
			default: 'p-4',
			sm: 'p-2',
		},
	},
	defaultVariants: {
		variant: 'default',
		direction: 'default',
		size: 'default',
	},
})

export interface CardProps
	extends React.ButtonHTMLAttributes<HTMLDivElement>,
		VariantProps<typeof cardVariants> {
	asChild?: boolean
	icon?: keyof typeof icons | string | null
	badge?: React.ReactNode
	clickable?: boolean
	href?: string
	target?: string
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
	(
		{
			className,
			variant,
			direction,
			size,
			asChild = false,
			icon,
			badge,
			clickable = false,
			href,
			target,
			...props
		},
		ref,
	) => {
		const isExternalLink = href?.startsWith('http') || href?.startsWith('//')
		const Comp = asChild ? Slot : href ? (isExternalLink ? 'a' : Link) : 'div'

		const isImagePath = (str: string) => {
			return /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(str)
		}

		return (
			<Comp
				className={cn(cardVariants({ variant, direction, size, className }), {
					'cursor-pointer transition-all duration-300 ease-in-out hover:shadow-sm':
						clickable || href,
					'hover:bg-primary': clickable || href,
					'hover:bg-accent': (clickable || href) && variant === 'filled',
					'hover:bg-muted/80': (clickable || href) && variant === 'empty',
					'hover:bg-red/10': (clickable || href) && variant === 'danger',
				})}
				// @ts-expect-error
				ref={ref}
				{...(isExternalLink ? { href, target } : { to: href })}
				target={target}
				{...props}
			>
				{icon && (
					<div
						className={cn(
							'flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-primary-foreground shadow-sm',
							size === 'sm' ? 'h-8 w-8' : 'h-12 w-12',
						)}
					>
						{typeof icon === 'string' && isImagePath(icon) ? (
							<img
								src={icon}
								alt="card-icon"
								className="h-full w-full shrink-0 object-cover"
							/>
						) : (
							<Icon
								name={icon as keyof typeof icons}
								className="h-5 w-5 shrink-0"
							/>
						)}
					</div>
				)}
				{badge && <div className="absolute right-4 top-4">{badge}</div>}
				{props.children}
			</Comp>
		)
	},
)

Card.displayName = 'Card'

export { Card, cardVariants }
